import axios, { AxiosInstance } from "axios";
import { v4 } from "uuid";
import { jwtDecode } from "jwt-decode";

import { store } from "../store/store";
import { setAuthToken } from "../store/slices/authSlice";
import {
  addNotification,
  removeAllNotifications,
} from "../store/slices/notificationSlice";
import {
  resetCurrentUser,
  setCurrentUser,
} from "../store/slices/currentUserSlice";
import { setCommonModalState } from "../store/slices/commonModalSlice";
import { clearClusterList } from "../store/slices/clusterListSlice";
import { clearCurrentCluster } from "../store/slices/currentClusterSlice";
import { clearApplications } from "../store/slices/applicationSlice";

import {
  AccessPayload,
  ApplicationInterface,
  ApplicationListResponse,
  AuthResponse,
  BonusBalanceInterface,
  BonusStatsInterface,
  BonusTransactionListResponse,
  CategoryListResponse,
  CityInterface,
  ClusterInterface,
  ComplexOrderInterface,
  ComplexOrderListResponse,
  CreateApplicationPayload,
  FullAddress,
  FullAddressInterface,
  ItemListResponse,
  LeadFormData,
  LoginResponse,
  OrderInterface,
  OrderListResponse,
  PromoListResponse,
  PublicStoreInterface,
  RegisterEmailPayload,
  SaleItemInterface,
  UpdateApplicationStatusPayload,
  UserInterface,
} from "../common/types/interfaces";
import { CommonModals, NotificationTypes } from "../common/types/enums";

const HOST = "https://logmall.ru";
const CORE_URL = `${HOST}/api/v1/data`;
const AUTH_URL = "https://logid.ru/api/v1/auth";

let api: Api | null = null;

class Api {
  private readonly core: AxiosInstance;
  private timezone: string;
  private currentCluster: ClusterInterface | null = null;

  constructor() {
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    this.core = axios.create({
      baseURL: CORE_URL,
      timeout: 30000,
      withCredentials: true,
    });

    this.attachInterceptors(this.core);
  }

  private attachInterceptors(instance: AxiosInstance) {
    instance.interceptors.request.use(
      (config) => {
        const token = store.getState().auth.token;
        if (token) config.headers.Authorization = `Bearer ${token}`;
        config.headers["X-Timezone"] = this.timezone;
        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          originalRequest &&
          error.response?.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          const result = await this.refresh().catch(() => null);
          if (result) {
            return instance(originalRequest);
          }
        }
        return Promise.reject(error);
      },
    );
  }

  private notifyError(text: string) {
    store.dispatch(
      addNotification({
        id: v4(),
        type: NotificationTypes.ERROR,
        text,
      }),
    );
  }

  private notifySuccess(text: string) {
    store.dispatch(
      addNotification({
        id: v4(),
        type: NotificationTypes.SUCCESS,
        text,
      }),
    );
  }

  // ============== Cluster ==============

  setCluster(cluster: ClusterInterface | null) {
    this.currentCluster = cluster;
  }

  getCluster() {
    return this.currentCluster;
  }

  getClusterImageUrl(): string {
    if (!this.currentCluster) return "";
    const { port, token, domain } = this.currentCluster;
    if (!port || !token) return "";
    return `https://${domain}/sales-api/${port}/${token}/api/v1/data/file`;
  }

  // ============== Token ==============

  setToken(token: string) {
    this.core.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  // ============== Session ==============

  private clearSession() {
    localStorage.removeItem("refresh");
    localStorage.removeItem("currentUser");

    store.dispatch(setAuthToken(""));
    store.dispatch(resetCurrentUser());
    store.dispatch(clearCurrentCluster());
    store.dispatch(clearClusterList());
    store.dispatch(clearApplications());
    store.dispatch(removeAllNotifications());

    delete this.core.defaults.headers.common["Authorization"];
    this.setCluster(null);
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem("refresh");
      await axios.get(`${AUTH_URL}/logout`, {
        headers: { Authorization: `Bearer ${refreshToken}` },
        withCredentials: true,
        timeout: 30000,
      });
    } catch {
      // ignore
    } finally {
      this.clearSession();
      this.notifyError("Вы вышли из аккаунта");
    }
  }

  forceLogout(): void {
    this.clearSession();
    this.notifyError("Пожалуйста, авторизуйтесь");
    store.dispatch(
      setCommonModalState({ modal: CommonModals.LOGIN, state: true }),
    );
  }

  async refresh(): Promise<LoginResponse | null> {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) return null;

    const result = await axios.get<LoginResponse>(`${AUTH_URL}/refresh`, {
      headers: { Authorization: `Bearer ${refreshToken}` },
      withCredentials: true,
      timeout: 30000,
    });

    await this.handleAuthResponse(result.data);
    return result.data;
  }

  // ============== Auth ==============

  async requestEmailCode(payload: {
    email: string;
  }): Promise<{ message: string }> {
    try {
      const result = await axios.post(`${AUTH_URL}/request-code`, payload, {
        withCredentials: true,
        timeout: 30000,
      });
      return result.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 429
          ? "Слишком много попыток, повторите позже"
          : "Не удалось отправить код";
      this.notifyError(message);
      throw error;
    }
  }

  async verifyEmailCode(payload: {
    email: string;
    code: string;
  }): Promise<AuthResponse> {
    try {
      const result = await axios.post<AuthResponse>(
        `${AUTH_URL}/verify-code`,
        payload,
        {
          withCredentials: true,
          timeout: 30000,
        },
      );
      await this.handleAuthResponse(result.data);
      return result.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 401
          ? "Неверный или просроченный код"
          : "Ошибка проверки кода";
      this.notifyError(message);
      throw error;
    }
  }

  async register(payload: RegisterEmailPayload): Promise<AuthResponse> {
    try {
      const result = await axios.post<AuthResponse>(
        `${AUTH_URL}/register`,
        payload,
        {
          withCredentials: true,
          timeout: 30000,
        },
      );
      await this.handleAuthResponse(result.data);
      return result.data;
    } catch (error) {
      this.notifyError("Ошибка регистрации");
      throw error;
    }
  }

  private getUserFromToken(token: string): UserInterface | null {
    try {
      const decoded = jwtDecode<AccessPayload>(token);
      return {
        id: decoded.userId,
        phone_number: decoded.phone_number || "",
        uuid: decoded.uuid || "",
        contacts: decoded.contacts || 0,
        email: decoded.email || "",
        user_clusters: decoded.user_clusters || [],
      };
    } catch {
      return null;
    }
  }

  private async handleAuthResponse(response: AuthResponse): Promise<void> {
    if (!response.accessToken || !response.refreshToken) return;

    const userFromToken = this.getUserFromToken(response.accessToken);
    if (userFromToken) {
      localStorage.setItem("currentUser", JSON.stringify(userFromToken));
      store.dispatch(setCurrentUser(userFromToken));
    }

    localStorage.setItem("refresh", response.refreshToken);
    store.dispatch(setAuthToken(response.accessToken));
    this.setToken(response.accessToken);
  }

  // ============== Clusters (log) ==============

  async getClusters(params?: {
    target?: "logplace" | "logmall";
    search?: string;
    cityId?: number;
  }): Promise<ClusterInterface[]> {
    try {
      const result = await this.core.get<ClusterInterface[]>("/cluster", {
        params: {
          target: params?.target,
          search: params?.search,
          "searchFields[]": params?.search
            ? ["name", "description"]
            : undefined,
          cityId: params?.cityId,
        },
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // ============== Stores (log) ==============

  async getStores(params?: {
    target?: "logplace" | "logmall";
    clusterId?: number;
    search?: string;
    cityId?: number;
  }): Promise<PublicStoreInterface[]> {
    try {
      const result = await this.core.get<{
        data: PublicStoreInterface[];
        total: number;
      }>("/store", {
        params: {
          ...params,
          "searchFields[]": params?.search
            ? ["name", "description", "address.value"]
            : undefined,
        },
      });
      return result.data.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить точки");
      throw error;
    }
  }

  async getStore(
    id: number,
    target?: "logplace" | "logmall",
  ): Promise<PublicStoreInterface> {
    try {
      const result = await this.core.get<PublicStoreInterface>(`/store/${id}`, {
        params: { target },
      });
      return result.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 404 ? "Точка не найдена" : "Не удалось загрузить точку";
      this.notifyError(message);
      throw error;
    }
  }

  // ============== LOGMALL: Categories ==============

  async getCategories(params?: {
    clusterId?: number;
    rootOnly?: boolean;
    cityId?: number;
  }): Promise<CategoryListResponse> {
    try {
      const result = await this.core.get<CategoryListResponse>(
        "/category/app",
        {
          params: {
            clusterId: params?.clusterId,
            rootOnly: params?.rootOnly ? "true" : undefined,
            cityId: params?.cityId,
          },
        },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить категории");
      throw error;
    }
  }

  // ============== LOGMALL: Items ==============

  async getItems(params?: {
    categoryId?: number;
    clusterId?: number;
    search?: string;
    limit?: number;
    offset?: number;
    cityId?: number;
    clusterType?: number;
    ids?: number[];
  }): Promise<ItemListResponse> {
    try {
      const result = await this.core.get<ItemListResponse>("/sale_item/app", {
        params: {
          ...params,
          ids: params?.ids?.length ? params.ids : undefined,
          "searchFields[]": params?.search
            ? ["name", "description", "barCode"]
            : undefined,
        },
      });
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить товары");
      throw error;
    }
  }

  async getItem(id: number): Promise<SaleItemInterface> {
    try {
      const result = await this.core.get<SaleItemInterface>(`/sale_item/${id}`);
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить товар");
      throw error;
    }
  }

  // ============== LOGMALL: Promos ==============

  async getPromos(params?: {
    search?: string;
    limit?: number;
    offset?: number;
    cityId?: number;
  }): Promise<PromoListResponse> {
    try {
      const result = await this.core.get<PromoListResponse>("/promo", {
        params: {
          ...params,
          "searchFields[]": params?.search
            ? ["name", "description"]
            : undefined,
        },
      });
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить акции");
      throw error;
    }
  }

  // ============== Applications (cluster) ==============

  async applyToVacancy(
    payload: CreateApplicationPayload,
  ): Promise<ApplicationInterface> {
    if (!this.currentCluster) {
      this.notifyError("Кластер не выбран");
      throw new Error("Cluster not selected");
    }

    const { domain, port, token } = this.currentCluster;
    const url = `https://${domain}/sales-api/${port}/${token}/api/v1/data/application`;

    try {
      const result = await axios.post<ApplicationInterface>(url, payload, {
        headers: {
          Authorization: store.getState().auth.token
            ? `Bearer ${store.getState().auth.token}`
            : "",
          "X-Timezone": this.timezone,
        },
        withCredentials: true,
        timeout: 30000,
      });
      this.notifySuccess("Отклик отправлен");
      return result.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 409
          ? "Вы уже откликались на эту вакансию"
          : "Не удалось отправить отклик";
      this.notifyError(message);
      throw error;
    }
  }

  async getMyApplications(): Promise<ApplicationListResponse> {
    try {
      const result =
        await this.core.get<ApplicationListResponse>("/application/me");
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить отклики");
      throw error;
    }
  }

  async getApplications(params?: {
    executorId?: number;
    status?: number;
    limit?: number;
    offset?: number;
  }): Promise<ApplicationListResponse> {
    try {
      const result = await this.core.get<ApplicationListResponse>(
        "/application",
        {
          params,
        },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить отклики");
      throw error;
    }
  }

  async updateApplicationStatus(
    id: number,
    payload: UpdateApplicationStatusPayload,
  ): Promise<ApplicationInterface> {
    try {
      const result = await this.core.patch<ApplicationInterface>(
        `/application/${id}/status`,
        payload,
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось обновить статус");
      throw error;
    }
  }

  // ============== Lead ==============

  async sendLead(payload: LeadFormData): Promise<{ success: boolean }> {
    try {
      const result = await this.core.post("/mail/send-lead", payload);
      this.notifySuccess("Заявка отправлена. Мы свяжемся с вами");
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось отправить заявку. Попробуйте позже");
      throw error;
    }
  }

  async getBannerItems(
    limit: number = 15,
    cityId?: number,
    clusterType?: number,
  ): Promise<SaleItemInterface[]> {
    try {
      const result = await this.core.get<SaleItemInterface[]>(
        "/sale_item/banner",
        {
          params: { limit, cityId, clusterType },
        },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить товары для баннера");
      throw error;
    }
  }

  async getCities(params?: {
    onlyWithPlaces?: boolean;
    target?: "logplace" | "logmall";
  }): Promise<CityInterface[]> {
    try {
      const result = await this.core.get<CityInterface[]>("/city", {
        params: {
          onlyWithPlaces: params?.onlyWithPlaces ? "true" : undefined,
          target: params?.target,
        },
      });
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить города");
      throw error;
    }
  }

  // ============== КАБИНЕТ ==============

  async getUserClusters(): Promise<
    { id: number; name: string; domain: string }[]
  > {
    try {
      const result =
        await this.core.get<{ id: number; name: string; domain: string }[]>(
          "/user/clusters",
        );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить компании");
      throw error;
    }
  }

  async getOrders(params?: {
    limit?: number;
    offset?: number;
    statuses?: number[];
    clusterId?: number;
  }): Promise<OrderInterface[]> {
    try {
      const result = await this.core.get<OrderInterface[]>("/order", {
        params,
      });
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить заказы");
      throw error;
    }
  }

  async getOrder(id: number): Promise<OrderInterface> {
    try {
      const result = await this.core.get<OrderInterface>(`/order/${id}`);
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить заказ");
      throw error;
    }
  }

  async getAddresses(): Promise<FullAddressInterface[]> {
    try {
      const result =
        await this.core.get<FullAddressInterface[]>("/full_address");
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить адреса");
      throw error;
    }
  }

  async getBonusBalances(): Promise<BonusBalanceInterface[]> {
    try {
      const result =
        await this.core.get<BonusBalanceInterface[]>("/bonus/balances");
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить бонусы");
      throw error;
    }
  }

  async getBonusTransactions(params?: {
    limit?: number;
    offset?: number;
  }): Promise<BonusTransactionListResponse> {
    try {
      const result = await this.core.get<BonusTransactionListResponse>(
        "/bonus/transactions",
        { params },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить историю бонусов");
      throw error;
    }
  }

  async getBonusHistory(
    clusterId: number,
    params?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<BonusTransactionListResponse> {
    try {
      const result = await this.core.get<BonusTransactionListResponse>(
        "/bonus/history",
        { params: { ...params, clusterId } },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить историю бонусов");
      throw error;
    }
  }

  async getBonusStats(clusterId: number): Promise<BonusStatsInterface> {
    try {
      const result = await this.core.get<BonusStatsInterface>("/bonus/stats", {
        params: { clusterId },
      });
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить статистику бонусов");
      throw error;
    }
  }

  async getComplexOrders(params?: {
    limit?: number;
    offset?: number;
    clusterId?: number;
    statuses?: number[];
  }): Promise<ComplexOrderInterface[]> {
    try {
      const result = await this.core.get<ComplexOrderInterface[]>(
        "/complex-order",
        { params },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить проекты");
      throw error;
    }
  }

  async getComplexOrder(id: number): Promise<ComplexOrderInterface> {
    try {
      const result = await this.core.get<ComplexOrderInterface>(
        `/complex-order/${id}`,
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить проект");
      throw error;
    }
  }

  async createAddress(payload: FullAddress): Promise<FullAddressInterface> {
    try {
      const result = await this.core.post<FullAddressInterface>(
        "/full_address",
        payload,
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось создать адрес");
      throw error;
    }
  }

  async updateAddress(
    id: number,
    payload: Partial<{
      name: string;
      description: string;
      isGeneral: boolean;
      entrance: string;
      floor: string;
      apartment: string;
      intercom: string;
      city: {
        id?: number;
        value: string;
        latitude?: string | number;
        longitude?: string | number;
      };
      address: {
        value: string;
        latitude?: string | number;
        longitude?: string | number;
      };
    }>,
  ): Promise<void> {
    try {
      await this.core.put(`/full_address/${id}`, payload);
    } catch (error) {
      this.notifyError("Не удалось обновить адрес");
      throw error;
    }
  }

  async deleteAddress(id: number): Promise<void> {
    try {
      await this.core.delete(`/full_address/${id}`);
    } catch (error) {
      this.notifyError("Не удалось удалить адрес");
      throw error;
    }
  }

  async setDefaultAddress(id: number): Promise<void> {
    try {
      await this.core.put(`/full_address/set-default/${id}`);
    } catch (error) {
      this.notifyError("Не удалось назначить адрес основным");
      throw error;
    }
  }

  async getFavorites(params?: {
    limit?: number;
    offset?: number;
    cityId?: number;
  }): Promise<ItemListResponse> {
    try {
      const result = await this.core.get<ItemListResponse>(
        "/sale_item/favorites",
        { params },
      );
      return result.data;
    } catch (error) {
      this.notifyError("Не удалось загрузить избранное");
      throw error;
    }
  }
  async toggleFavorite(itemId: number): Promise<{ isActive: boolean }> {
    try {
      const result = await this.core.post<{
        isActive: boolean;
        message?: string;
      }>(`/sale_item/${itemId}/favorite`);
      return { isActive: result.data.isActive };
    } catch (error) {
      this.notifyError("Не удалось обновить избранное");
      throw error;
    }
  }

  async createOrder(cluster: any, payload: any): Promise<any> {
    const { domain, port, token } = cluster || {};
    if (!domain || !port || !token) {
      throw new Error("Cluster is not configured");
    }
    const url = `https://${domain}/sales-api/${port}/${token}/api/v1/data/order/web`;
    const result = await this.core.post(url, payload);
    return result.data;
  }

  async cdekCalculate(payload: any): Promise<any> {
    const result = await this.core.post("/cdek/calculate", payload);
    return result.data;
  }
}

export const getApi = () => {
  if (!api) api = new Api();
  return api;
};
